import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

import type {
  CatalogProfileDetailDto,
  SeniorityLevelDto,
} from '@/api/catalog-client';
import type {
  DashboardParticipationDto,
  DashboardPhaseDto,
} from '@/api/supplier-dashboard-client';
import type {
  BriefingCycleDto,
  BriefingCycleStatusDto,
  SupplierBriefingDto,
} from '@/api/supplier-conditions-client';
import { useCurrentCatalogReferential } from '@/api/use-catalog';
import { useSupplierConditions } from '@/api/use-supplier-conditions';
import { useSupplierDashboard } from '@/api/use-supplier-dashboard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
/**
 * Rang canonique d'un niveau pour l'ordre d'affichage, TOLÉRANT à la représentation (accentuée
 * « Débutant » ou non « Debutant ») — les deux BC divergent (L-076). On normalise (sans diacritiques,
 * minuscule) avant de comparer à l'ordre Débutant→Référent. Inconnu → en fin de liste.
 */
const NIVEAU_CANON_ORDER = ['debutant', 'maitrisant', 'expert', 'referent'];
function niveauRank(value: string): number {
  const normalized = value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
  const index = NIVEAU_CANON_ORDER.indexOf(normalized);
  return index === -1 ? NIVEAU_CANON_ORDER.length : index;
}
import { cn } from '@/lib/utils';
import {
  buildFamilyTree,
  correlatedProfiles,
  filterProfiles,
  indexByCode,
  type CatalogReferentialSearch,
} from '@/routes/supplier/catalog/catalog-referential-model';
import { FamilyNav } from '@/routes/supplier/catalog/family-nav';
import { ProfileDetailPanel } from '@/routes/supplier/catalog/profile-detail-panel';
import { TjmGridPanel } from '@/routes/supplier/rate-collection/tjm-grid-panel';
import {
  briefingDescriptionByCode,
  buildPerimeterProfiles,
  deriveCampaignPeriod,
  perimeterProfileCodes,
  type CampaignPeriod,
} from '@/routes/supplier/campaigns/supplier-campaigns-model';

// =============================================================================
// supplier-campaigns-screen.tsx — Page FOURNISSEUR « Mes campagnes » (GATE_HUMAN 2026-09-12, maquette #83).
// =============================================================================
// PHASE A+B de la refonte : en-tête condensé + tableau (colonne Statut FUSIONNÉE) + panneau de détail avec
// BANDE D'INFORMATIONS 4 colonnes + périmètre métier resserré (2 filtres, familles masquées si mono-famille,
// grille de cartes fluide). UI-ONLY : aucune modification API/contrat/back — les mêmes read-models (dashboard
// `active[]`/`past[]`, conditions `SupplierBriefingDto`, référentiel catalogue) sont RÉ-AGENCÉS.
//
// Héritage GATE_HUMAN 2026-09-11 (données, INCHANGÉ) :
//   • Liste = dashboard `active[]`/`past[]`, pré-filtre par `statut`.
//   • Détail = read-model conditions par campaignId, joint au dashboard pour submissionId/cycle/phase (CTA saisie).
//   • Dates campagne = DÉRIVÉES des cycles (min openAt / max closeAt) — le read-model ne les porte pas.
//   • Contact JeKo = GÉNÉRIQUE (lien /echanges + email contact@jeko.biz) — pas de contact par-campagne dans l'API.
//   • Périmètre = structure du CATALOGUE filtrée aux profileCodes du périmètre.
//
// GATE_HUMAN 2026-09-12 (maquette #83, PHASE A+B) — AUCUNE PERTE D'INFORMATION :
//   • Colonne « Statut » FUSIONNE l'ex-« État » + « Phase » ; l'ex-« (cycle N) » de l'état est CONSERVÉ dans le
//     libellé fusionné (« Saisie ouverte · cycle N »). Un SEUL style de badge (fond ivoire, bordure fine).
//   • Sous-ligne de SYNTHÈSE (période + N cycles + N profils) ; les DÉTAILS (dates par cycle, familles) restent
//     dans la bande d'informations — rien n'est perdu, seulement ré-agencé.
//   • CTA de saisie CONSERVÉ tel quel (PHASE C ultérieure fusionnera la grille TJM ici).
//
// WCAG 2.1 AA : <table> avec <caption>, <th scope>, ligne sélectionnable clavier (Entrée/Espace), états en
// LIBELLÉ TEXTE (badge = renfort, jamais une couleur seule), chevron décoratif (aria-hidden ; l'état est porté
// par aria-selected), messages role="status"/role="alert", filtres <label> liés. Tokens sémantiques
// shadcn/Tailwind uniquement, échelle de spacing (pas de magic numbers). Aucun état métier en localStorage
// (RULE_REF_006 : état de vue = URL/TanStack).
//
// Anti-LLMism (invariant #6) : aucun em-dash comme substitut parenthétique dans le contenu produit — les
// séparateurs sont le middot « · », les plages de dates l'en-dash « – ». La maquette #83 affichait l'état de
// cycle après un em-dash (« … — clos ») ; remplacé par « · clos » (mono) pour respecter l'invariant. Info
// intégralement conservée.

/** Aucun CTA de saisie dans le panneau catalogue de cette page (la saisie est atteinte au grain CAMPAGNE). */
const PANEL_NO_SAISIE = (): void => {};

/** Classe partagée des <select> natifs (tokens sémantiques ; focus visible clavier). */
const SELECT_CLASS = cn(
  'h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
);

/** Filtre de pré-tri de la liste : campagnes « en cours » (défaut) vs « archivées ». */
export type SupplierCampaignsStatut = 'en-cours' | 'archive';

/** Les deux valeurs de filtre de statut (alimente le <select>). */
export const SUPPLIER_CAMPAIGNS_STATUTS: readonly SupplierCampaignsStatut[] = [
  'en-cours',
  'archive',
];

/** Libellé FR du filtre de statut (jamais une couleur seule ; le libellé porte l'information). */
function statutLabel(statut: SupplierCampaignsStatut): string {
  return statut === 'archive' ? 'Archivées' : 'En cours';
}

/** Libellé FR de la phase opérationnelle (parité dashboard-screen, DBU §3.2). */
function phaseLabel(phase: DashboardPhaseDto): string {
  switch (phase) {
    case 'Ouverture':
      return 'Saisie ouverte';
    case 'Analyse':
      return 'Analyse en cours';
    case 'Negociation':
      return 'Négociation';
    case 'Cloture':
      return 'Clôturée';
  }
}

/**
 * Libellé de statut FUSIONNÉ (maquette #83, point 4) : ex-« État » + « Phase » en UN seul libellé. Les phases
 * liées à un cycle (Ouverture / Analyse) CONSERVENT le numéro de cycle (« Saisie ouverte · cycle N ») — c'est
 * l'information portée par l'ex-colonne « État » (« Ouverte (cycle N) »), donc AUCUNE PERTE. Négociation /
 * Clôture ne sont pas indexées sur un cycle ⇒ pas de suffixe.
 */
function fusedStatusLabel(phase: DashboardPhaseDto, cycleNumber: number): string {
  const base = phaseLabel(phase);
  if (phase === 'Ouverture' || phase === 'Analyse') {
    return `${base} · cycle ${String(cycleNumber)}`;
  }
  return base;
}

/** Formate une date ISO 8601 en date FR ABRÉGÉE (« 10 sept. 2026 »), ou « non planifiée » si `null`. */
function formatDate(iso: string | null): string {
  if (iso === null) {
    return 'Non planifiée';
  }
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Formate la période d'une campagne (maquette #83, point 8) de façon COMPACTE : une seule date si période =
 * 1 jour ; « 9 – 10 sept. 2026 » (jour de début seul) si même mois/année ; sinon plage complète. En-dash « – »
 * = séparateur de plage (jamais un em-dash, invariant #6).
 */
function formatCampaignPeriod(period: CampaignPeriod): string {
  const { start, end } = period;
  if (start === null && end === null) {
    return 'Période non planifiée';
  }
  if (start === null) {
    return formatDate(end);
  }
  if (end === null) {
    return formatDate(start);
  }
  const startLabel = formatDate(start);
  const endLabel = formatDate(end);
  if (startLabel === endLabel) {
    return startLabel;
  }
  const s = new Date(start);
  const e = new Date(end);
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${String(s.getDate())} – ${endLabel}`;
  }
  return `${startLabel} – ${endLabel}`;
}

/** Date d'un cycle (maquette #83, point 12) : une SEULE date quand début = fin (même jour), sinon plage. */
function formatCycleDate(cycle: BriefingCycleDto): string {
  const { openAt, closeAt } = cycle;
  if (openAt === null && closeAt === null) {
    return 'Non planifié';
  }
  if (openAt === null) {
    return formatDate(closeAt);
  }
  if (closeAt === null) {
    return formatDate(openAt);
  }
  const openLabel = formatDate(openAt);
  const closeLabel = formatDate(closeAt);
  return openLabel === closeLabel ? openLabel : `${openLabel} – ${closeLabel}`;
}

/**
 * État de cycle en LIBELLÉ FR (maquette #83, point 12) DÉRIVÉ de la donnée EXISTANTE `status`
 * (AVenir/EnCours/Termine — autorité API). On n'INVENTE aucun état : « dépassé » de la maquette relèverait
 * d'un calcul date>close hors contrat, donc on relaie le statut serveur (à venir / ouvert / clos).
 */
function cycleStatusLabel(status: BriefingCycleStatusDto): string {
  switch (status) {
    case 'AVenir':
      return 'à venir';
    case 'EnCours':
      return 'ouvert';
    case 'Termine':
      return 'clos';
  }
}

/** Accord grammatical « N profil(s) ». */
function profilesLabel(count: number): string {
  return `${String(count)} profil${count > 1 ? 's' : ''}`;
}

export interface SupplierCampaignsScreenProps {
  /** Filtre de statut courant (search param d'URL ; défaut « en-cours »). */
  readonly statut: SupplierCampaignsStatut;
  /** Change le filtre de statut (l'adaptateur routeur met à jour l'URL). */
  readonly onChangeStatut: (statut: SupplierCampaignsStatut) => void;
  /** Campagne sélectionnée (search param `campaign`) ; `undefined` = aucune. */
  readonly selectedCampaignId: string | undefined;
  /** Sélectionne / désélectionne une campagne (l'adaptateur routeur sérialise `campaign`). */
  readonly onSelectCampaign: (campaignId: string | undefined) => void;
  /** État de vue du catalogue de périmètre (search params famille/sousFamille/niveau/q/profil). */
  readonly catalogSearch: CatalogReferentialSearch;
  /** Remplace l'état de vue du catalogue (l'adaptateur routeur le sérialise dans l'URL). */
  readonly onChangeCatalogSearch: (next: CatalogReferentialSearch) => void;
}

export function SupplierCampaignsScreen({
  statut,
  onChangeStatut,
  selectedCampaignId,
  onSelectCampaign,
  catalogSearch,
  onChangeCatalogSearch,
}: SupplierCampaignsScreenProps): React.JSX.Element {
  const dashboard = useSupplierDashboard();
  const conditions = useSupplierConditions();

  const participations = React.useMemo<readonly DashboardParticipationDto[]>(() => {
    if (dashboard.data === undefined) {
      return [];
    }
    return statut === 'archive' ? dashboard.data.past : dashboard.data.active;
  }, [dashboard.data, statut]);

  const selectedParticipation =
    selectedCampaignId === undefined
      ? undefined
      : participations.find((p) => p.campaignId === selectedCampaignId);

  const selectedBriefing =
    selectedCampaignId === undefined
      ? undefined
      : conditions.data?.campaigns.find(
          (c) => c.campaignId === selectedCampaignId,
        );

  return (
    <section className="flex flex-col gap-6" aria-labelledby="campaigns-title">
      {/* En-tête (points 1-2) : titre + sous-titre à gauche ; contrôle Statut COMPACT à droite, même ligne. */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1
            id="campaigns-title"
            className="text-2xl font-semibold tracking-tight text-foreground"
          >
            Mes campagnes
          </h1>
          <p className="text-sm text-muted-foreground">
            Sélectionnez une campagne pour voir son récapitulatif et les profils
            concernés.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label
            htmlFor="campaigns-statut-filter"
            className="text-sm font-medium text-muted-foreground"
          >
            Statut
          </Label>
          <select
            id="campaigns-statut-filter"
            name="statut"
            value={statut}
            onChange={(e) => {
              const next = e.target.value;
              if ((SUPPLIER_CAMPAIGNS_STATUTS as readonly string[]).includes(next)) {
                onChangeStatut(next as SupplierCampaignsStatut);
              }
            }}
            className={cn(SELECT_CLASS, 'w-auto')}
          >
            {SUPPLIER_CAMPAIGNS_STATUTS.map((value) => (
              <option key={value} value={value}>
                {statutLabel(value)}
              </option>
            ))}
          </select>
        </div>
      </header>

      {/* Point 3 : plus de sous-titre « Campagnes » — la table suit immédiatement. */}
      <CampaignsList
        participations={participations}
        isPending={dashboard.isPending}
        isError={dashboard.isError}
        errorMessage={dashboard.error?.message}
        statut={statut}
        selectedCampaignId={selectedCampaignId}
        onSelectCampaign={onSelectCampaign}
      />

      {selectedParticipation !== undefined ? (
        <CampaignDetail
          participation={selectedParticipation}
          briefing={selectedBriefing}
          briefingPending={conditions.isPending}
          briefingError={conditions.isError ? conditions.error.message : null}
          catalogSearch={catalogSearch}
          onChangeCatalogSearch={onChangeCatalogSearch}
          onClose={() => onSelectCampaign(undefined)}
        />
      ) : null}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Registre (liste) — sélection de LIGNE ; colonne Statut FUSIONNÉE (maquette #83, points 4-6)
// -----------------------------------------------------------------------------

interface CampaignsListProps {
  readonly participations: readonly DashboardParticipationDto[];
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly errorMessage: string | undefined;
  readonly statut: SupplierCampaignsStatut;
  readonly selectedCampaignId: string | undefined;
  readonly onSelectCampaign: (campaignId: string | undefined) => void;
}

/** Badge de statut UNIFORME (fond ivoire, bordure fine) — parité dashboard StatusBadge (point 4). */
function StatusBadge({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <span className="inline-flex items-center rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium text-foreground">
      {children}
    </span>
  );
}

function CampaignsList({
  participations,
  isPending,
  isError,
  errorMessage,
  statut,
  selectedCampaignId,
  onSelectCampaign,
}: CampaignsListProps): React.JSX.Element {
  if (isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Chargement de vos campagnes…
      </p>
    );
  }
  if (isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Erreur de chargement : {errorMessage ?? 'indisponible'}.
      </p>
    );
  }
  if (participations.length === 0) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        {statut === 'archive'
          ? 'Aucune campagne archivée.'
          : 'Aucune campagne de référencement ne vous est actuellement ouverte.'}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Liste de vos campagnes (intitulé, statut, échéance) — sélectionnez une ligne
          pour afficher le détail
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            <th scope="col" className="px-4 py-3 font-medium">
              Intitulé
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Statut
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Échéance du cycle
            </th>
            <th scope="col" className="w-10 px-2 py-3">
              <span className="sr-only">Ouvrir le détail</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {participations.map((participation) => {
            const isSelected = selectedCampaignId === participation.campaignId;
            const Chevron = isSelected ? ChevronUp : ChevronDown;
            return (
              <tr
                key={participation.campaignId}
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => {
                  onSelectCampaign(
                    isSelected ? undefined : participation.campaignId,
                  );
                }}
                onKeyDown={(e) => {
                  if (
                    (e.key === 'Enter' || e.key === ' ') &&
                    e.target === e.currentTarget
                  ) {
                    e.preventDefault();
                    onSelectCampaign(
                      isSelected ? undefined : participation.campaignId,
                    );
                  }
                }}
                className={cn(
                  'cursor-pointer border-b border-l-2 border-border last:border-b-0',
                  'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  isSelected
                    ? 'border-l-primary bg-muted/50'
                    : 'border-l-transparent',
                )}
              >
                <td className="px-4 py-3 align-top font-medium text-foreground">
                  {participation.intitule}
                </td>
                <td className="px-4 py-3 align-top">
                  <StatusBadge>
                    {fusedStatusLabel(participation.phase, participation.cycleNumber)}
                  </StatusBadge>
                </td>
                <td className="px-4 py-3 align-top text-muted-foreground">
                  {formatDate(participation.cycleCloseAt)}
                </td>
                <td className="px-2 py-3 align-top text-muted-foreground">
                  <Chevron className="h-4 w-4" aria-hidden="true" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Détail campagne — en-tête (titre serif + badge + synthèse + actions) puis bande info + périmètre
// -----------------------------------------------------------------------------

interface CampaignDetailProps {
  readonly participation: DashboardParticipationDto;
  readonly briefing: SupplierBriefingDto | undefined;
  readonly briefingPending: boolean;
  readonly briefingError: string | null;
  readonly catalogSearch: CatalogReferentialSearch;
  readonly onChangeCatalogSearch: (next: CatalogReferentialSearch) => void;
  readonly onClose: () => void;
}

/** Sous-ligne de SYNTHÈSE (point 8) : période dérivée · N cycles · N profils. */
function synthesisLine(briefing: SupplierBriefingDto): string {
  const periodLabel = formatCampaignPeriod(deriveCampaignPeriod(briefing.cycles));
  const cycles = briefing.cycles.length;
  const profiles = briefing.perimetre.totalProfiles;
  return `${periodLabel} · ${String(cycles)} cycle${cycles > 1 ? 's' : ''} · ${profilesLabel(profiles)}`;
}

function CampaignDetail({
  participation,
  briefing,
  briefingPending,
  briefingError,
  catalogSearch,
  onChangeCatalogSearch,
  onClose,
}: CampaignDetailProps): React.JSX.Element {
  return (
    <section
      aria-labelledby="campaign-detail-heading"
      className="flex flex-col gap-6 rounded-lg border border-border p-6"
    >
      {/* En-tête (points 7-9) : titre serif + badge fusionné + synthèse à gauche ; action Fermer à droite.
          PHASE C (GATE_HUMAN 2026-09-12) : le CTA de saisie est RETIRÉ — la grille TJM est désormais
          embarquée EN DESSOUS du périmètre (composant réutilisable <TjmGridPanel/>). */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-3">
            <h2
              id="campaign-detail-heading"
              className="font-serif text-xl font-semibold tracking-tight text-foreground"
            >
              {participation.intitule}
            </h2>
            <StatusBadge>
              {fusedStatusLabel(participation.phase, participation.cycleNumber)}
            </StatusBadge>
          </div>
          {briefing !== undefined ? (
            <p className="text-sm text-muted-foreground">{synthesisLine(briefing)}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>

      {briefingPending ? (
        <p role="status" className="text-sm text-muted-foreground">
          Chargement du récapitulatif…
        </p>
      ) : briefingError !== null ? (
        <p role="alert" className="text-sm text-destructive">
          Récapitulatif indisponible : {briefingError}.
        </p>
      ) : briefing === undefined ? (
        <p role="status" className="text-sm text-muted-foreground">
          Le récapitulatif détaillé de cette campagne n'est pas disponible.
        </p>
      ) : (
        <>
          <CampaignInfoBand briefing={briefing} />
          <CampaignPerimeter
            briefing={briefing}
            search={catalogSearch}
            onChange={onChangeCatalogSearch}
          />
        </>
      )}

      {/* PHASE C — GRILLE TJM embarquée SOUS le périmètre métier (composant réutilisable). Réutilise le
          submissionId de la campagne (read-model dashboard) + son cycle. Variante « embedded » : pas de
          double titre (l'en-tête campagne existe déjà). Le filtre famille de la grille est géré en interne. */}
      <TjmGridPanel
        submissionId={participation.submissionId}
        cycle2={participation.cycleNumber >= 2}
        cycleNumber={participation.cycleNumber}
        variant="embedded"
        headingId="campaign-tjm-grid-heading"
      />
    </section>
  );
}

// -----------------------------------------------------------------------------
// Bande d'informations — 4 colonnes, labels mono MAJ (maquette #83, points 11-14)
// -----------------------------------------------------------------------------

/** Une colonne de la bande info : label mono MAJUSCULE (parité en-têtes ds-table) + contenu. */
function InfoCol({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <dt className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

function CampaignInfoBand({
  briefing,
}: {
  readonly briefing: SupplierBriefingDto;
}): React.JSX.Element {
  const { clients } = briefing;
  const familyNames = briefing.perimetre.families
    .map((f) => f.family)
    .filter((name): name is string => name !== null && name.length > 0);
  const profileCount = briefing.perimetre.totalProfiles;

  return (
    <dl
      aria-label="Informations de la campagne"
      className="grid gap-6 rounded-lg border border-border bg-muted/20 p-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      {/* 11. CLIENT(S) BÉNÉFICIAIRE(S) — divulgués si le gate admin l'autorise, sinon « Non divulgué ». */}
      <InfoCol label="Client(s) bénéficiaire(s)">
        {clients.diffusionEnabled && clients.beneficiaries.length > 0 ? (
          <ul className="flex flex-col gap-0.5">
            {clients.beneficiaries.map((b) => (
              <li key={b.siren}>{b.raisonSociale}</li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col gap-0.5">
            <span>Non divulgué</span>
            <span className="text-xs text-muted-foreground">
              Selon paramétrage administrateur
            </span>
          </div>
        )}
      </InfoCol>

      {/* 12. CYCLES — une ligne par cycle : « Cycle N · date(s) » + état (mono) DÉRIVÉ du statut serveur. */}
      <InfoCol label="Cycles">
        <ul className="flex flex-col gap-1.5">
          {briefing.cycles.map((cycle) => (
            <li key={cycle.cycleNumber} className="flex flex-wrap items-baseline gap-x-1.5">
              <span className="font-medium text-foreground">
                Cycle {cycle.cycleNumber}
              </span>
              <span className="text-muted-foreground">· {formatCycleDate(cycle)}</span>
              <span className="font-mono text-xs text-muted-foreground">
                · {cycleStatusLabel(cycle.status)}
              </span>
            </li>
          ))}
        </ul>
      </InfoCol>

      {/* 13. PROFILS CONCERNÉS — compte + nom(s) de famille du périmètre (la famille n'est plus sur les cartes). */}
      <InfoCol label="Profils concernés">
        <span>
          {profilesLabel(profileCount)}
          {familyNames.length > 0 ? ` · ${familyNames.join(', ')}` : ''}
        </span>
      </InfoCol>

      {/* 14. CONTACT JEKO — email générique (mailto) + accès messagerie. Pas de contact par-campagne dans l'API. */}
      <InfoCol label="Contact JeKo">
        {/* Maquette #91 : « Ouvrir une conversation » INLINE, à côté de l'email (séparateur ·). */}
        <div className="flex flex-wrap items-center gap-x-1.5 text-sm">
          <a
            href="mailto:contact@jeko.biz"
            className="text-foreground underline underline-offset-4 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            contact@jeko.biz
          </a>
          <span aria-hidden="true" className="text-muted-foreground">
            ·
          </span>
          <Link
            to="/echanges"
            search={{ campaign: briefing.campaignId }}
            className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Ouvrir une conversation
          </Link>
        </div>
      </InfoCol>
    </dl>
  );
}

// -----------------------------------------------------------------------------
// Périmètre métier = CATALOGUE NAVIGABLE filtré aux profils de la campagne (maquette #83, points 15-22)
// -----------------------------------------------------------------------------

interface CampaignPerimeterProps {
  readonly briefing: SupplierBriefingDto;
  readonly search: CatalogReferentialSearch;
  readonly onChange: (next: CatalogReferentialSearch) => void;
}

function CampaignPerimeter({
  briefing,
  search,
  onChange,
}: CampaignPerimeterProps): React.JSX.Element {
  const query = useCurrentCatalogReferential();

  // `patch` fusionne puis COMPACTE (drop des clés `undefined`) — parité catalog-referential-screen : sous
  // `exactOptionalPropertyTypes`, une clé absente ≠ une clé à `undefined` (URL propre, aucun `?q=undefined`).
  const patch = React.useCallback(
    (
      changes: Partial<{
        famille: string | undefined;
        sousFamille: string | undefined;
        niveau: SeniorityLevelDto | undefined;
        q: string | undefined;
        profil: string | undefined;
      }>,
    ): void => {
      const merged = { ...search, ...changes };
      const next: {
        famille?: string;
        sousFamille?: string;
        niveau?: SeniorityLevelDto;
        q?: string;
        profil?: string;
      } = {};
      if (merged.famille !== undefined) next.famille = merged.famille;
      if (merged.sousFamille !== undefined) next.sousFamille = merged.sousFamille;
      if (merged.niveau !== undefined) next.niveau = merged.niveau;
      if (merged.q !== undefined && merged.q.length > 0) next.q = merged.q;
      if (merged.profil !== undefined) next.profil = merged.profil;
      onChange(next);
    },
    [onChange, search],
  );

  const onSelectFamille = React.useCallback(
    (famille: string | undefined): void => {
      patch({ famille, sousFamille: undefined });
    },
    [patch],
  );
  const onSelectSousFamille = React.useCallback(
    (sousFamille: string | undefined): void => patch({ sousFamille }),
    [patch],
  );
  const onSelectNiveau = React.useCallback(
    (niveau: SeniorityLevelDto | undefined): void => patch({ niveau }),
    [patch],
  );
  const onSearch = React.useCallback((q: string): void => patch({ q }), [patch]);
  const onReset = React.useCallback((): void => {
    onChange(search.profil === undefined ? {} : { profil: search.profil });
  }, [onChange, search]);
  const onSelectProfile = React.useCallback(
    (profileCode: string): void => patch({ profil: profileCode }),
    [patch],
  );
  const onCloseProfile = React.useCallback(
    (): void => patch({ profil: undefined }),
    [patch],
  );

  // Périmètre = INTERSECTION structure catalogue × codes du périmètre. Description au clic surchargée par
  // l'« attendu » du briefing quand présent (sinon description catalogue).
  const allProfiles = React.useMemo(() => query.data?.profiles ?? [], [query.data]);
  const correlations = React.useMemo(
    () => query.data?.correlations ?? [],
    [query.data],
  );
  const perimeterProfiles = React.useMemo(() => {
    const codes = perimeterProfileCodes(briefing);
    const descByCode = briefingDescriptionByCode(briefing);
    return buildPerimeterProfiles(allProfiles, codes, descByCode);
  }, [allProfiles, briefing]);

  const familles = React.useMemo(
    () => buildFamilyTree(perimeterProfiles),
    [perimeterProfiles],
  );
  const byCode = React.useMemo(
    () => indexByCode(perimeterProfiles),
    [perimeterProfiles],
  );
  const filtered = React.useMemo(
    () => filterProfiles(perimeterProfiles, search),
    [perimeterProfiles, search],
  );
  // BUGFIX 2026-09-12 : les options du filtre Niveau sont DÉRIVÉES des `availableLevels` réels du
  // périmètre (mêmes valeurs que celles comparées par filterProfiles). Auparavant le select utilisait
  // SENIORITY_LEVELS (accentués « Débutant… ») alors que availableLevels est non-accentué (« Debutant… »,
  // représentation campaign) → `includes` toujours faux → filtrer vidait tout. On garde l'ordre canonique.
  const niveauOptions = React.useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const p of perimeterProfiles) {
      for (const level of p.availableLevels) {
        if (!seen.has(level)) {
          seen.add(level);
          out.push(level);
        }
      }
    }
    return out.sort((a, b) => niveauRank(a) - niveauRank(b));
  }, [perimeterProfiles]);
  const selectedProfile =
    search.profil === undefined ? null : (byCode.get(search.profil) ?? null);
  const correlated = React.useMemo(
    () =>
      selectedProfile === null
        ? []
        : correlatedProfiles(selectedProfile.profileCode, correlations, byCode),
    [selectedProfile, correlations, byCode],
  );

  // Point 17 : navigation par famille pertinente UNIQUEMENT à partir de DEUX familles couvertes (sinon la
  // sidebar + les selects Famille/Sous-famille sont masqués — la campagne est mono-famille).
  const multiFamily = familles.length >= 2;

  // Point 18 : « Réinitialiser » n'apparaît QU'avec ≥ 1 filtre actif.
  const hasActiveFilters =
    search.famille !== undefined ||
    search.sousFamille !== undefined ||
    search.niveau !== undefined ||
    (search.q !== undefined && search.q.length > 0);

  // Sous-familles du <select> = celles de la famille sélectionnée (parité CatalogFilters).
  const sousFamilles =
    search.famille === undefined
      ? []
      : (familles.find((node) => node.key === search.famille)?.sousFamilles ?? []);

  return (
    <section aria-labelledby="campaign-perimeter-heading" className="flex flex-col gap-4">
      {/* Point 15-16 : titre + compteur mono à gauche ; filtres réduits à DEUX (recherche + niveau) sur la
          MÊME ligne ; Famille/Sous-famille RÉAPPARAISSENT à ≥ 2 familles ; Réinitialiser conditionnel. */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-baseline gap-3">
          <h3
            id="campaign-perimeter-heading"
            className="text-base font-semibold tracking-tight text-foreground"
          >
            Périmètre métier
          </h3>
          <span className="font-mono text-xs text-muted-foreground">
            {profilesLabel(perimeterProfiles.length)}
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="perimeter-search" className="sr-only">
              Rechercher un profil
            </Label>
            <Input
              id="perimeter-search"
              type="search"
              placeholder="Libellé, code JeKo, mot-clé…"
              value={search.q ?? ''}
              onChange={(event) => onSearch(event.target.value)}
              className="w-full sm:w-64"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="perimeter-niveau" className="sr-only">
              Niveau d'expertise
            </Label>
            <select
              id="perimeter-niveau"
              className={SELECT_CLASS}
              value={search.niveau ?? ''}
              onChange={(event) =>
                onSelectNiveau(
                  event.target.value === ''
                    ? undefined
                    : (event.target.value as SeniorityLevelDto),
                )
              }
            >
              <option value="">Niveau · Tous</option>
              {niveauOptions.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          {multiFamily ? (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="perimeter-famille" className="sr-only">
                  Famille
                </Label>
                <select
                  id="perimeter-famille"
                  className={SELECT_CLASS}
                  value={search.famille ?? ''}
                  onChange={(event) =>
                    onSelectFamille(
                      event.target.value === '' ? undefined : event.target.value,
                    )
                  }
                >
                  <option value="">Famille · Toutes</option>
                  {familles.map((node) => (
                    <option key={node.key} value={node.key}>
                      {node.key}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="perimeter-sous-famille" className="sr-only">
                  Sous-famille
                </Label>
                <select
                  id="perimeter-sous-famille"
                  className={SELECT_CLASS}
                  value={search.sousFamille ?? ''}
                  disabled={search.famille === undefined}
                  onChange={(event) =>
                    onSelectSousFamille(
                      event.target.value === '' ? undefined : event.target.value,
                    )
                  }
                >
                  <option value="">Sous-famille · Toutes</option>
                  {sousFamilles.map((node) => (
                    <option key={node.key} value={node.key}>
                      {node.key}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : null}

          {hasActiveFilters ? (
            <Button type="button" variant="outline" size="sm" onClick={onReset}>
              Réinitialiser les filtres
            </Button>
          ) : null}
        </div>
      </div>

      {query.isPending ? (
        <p role="status" className="text-sm text-muted-foreground">
          Chargement du catalogue des profils…
        </p>
      ) : null}

      {query.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Impossible de charger le catalogue des profils : {query.error.message}.
        </p>
      ) : null}

      {query.isSuccess ? (
        perimeterProfiles.length === 0 ? (
          <p
            role="status"
            className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground"
          >
            Aucun profil du périmètre n'est disponible dans le catalogue courant.
          </p>
        ) : multiFamily ? (
          <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
            <div className="lg:sticky lg:top-6 lg:self-start">
              <FamilyNav
                familles={familles}
                selectedFamille={search.famille}
                selectedSousFamille={search.sousFamille}
                onSelectFamille={onSelectFamille}
                onSelectSousFamille={onSelectSousFamille}
              />
            </div>
            <CampaignProfileCards
              profiles={filtered}
              selectedProfileCode={search.profil}
              onSelectProfile={onSelectProfile}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <CampaignProfileCards
              profiles={filtered}
              selectedProfileCode={search.profil}
              onSelectProfile={onSelectProfile}
            />
            {/* Point 17 : note explicative tant que la campagne est mono-famille. */}
            <p className="text-xs text-muted-foreground">
              Filtres Famille / Sous-famille masqués tant que la campagne ne couvre
              qu'une famille.
            </p>
          </div>
        )
      ) : null}

      {/* Clic → fiche : réutilise la fiche catalogue (description = attendu). PAS de CTA saisie ici
          (la saisie est atteinte au grain CAMPAGNE, en tête de détail). */}
      <ProfileDetailPanel
        profile={selectedProfile}
        correlated={correlated}
        onSelectCorrelated={onSelectProfile}
        onClose={onCloseProfile}
        tjmEntryTarget={null}
        onSaisirTjm={PANEL_NO_SAISIE}
      />
    </section>
  );
}

// -----------------------------------------------------------------------------
// Cartes de profil — grille FLUIDE auto-fill (maquette #83, points 19-22)
// -----------------------------------------------------------------------------

/**
 * Grille de cartes FLUIDE (point 19) : `repeat(auto-fill, minmax(320px, 1fr))` — passe à la ligne, plus de
 * défilement horizontal. Carte (points 20-22) : ligne mono code (gauche) / sous-famille (droite) — la FAMILLE
 * n'est plus répétée (elle est dans PROFILS CONCERNÉS) ; nom serif 16px ; chips de niveau TOUS au style NEUTRE
 * (décision GATE : aucun ambre, l'ambre signalait une info manquante, pas un niveau attendu — pas de légende).
 */
function CampaignProfileCards({
  profiles,
  selectedProfileCode,
  onSelectProfile,
}: {
  readonly profiles: readonly CatalogProfileDetailDto[];
  readonly selectedProfileCode: string | undefined;
  readonly onSelectProfile: (profileCode: string) => void;
}): React.JSX.Element {
  if (profiles.length === 0) {
    return (
      <p
        role="status"
        className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground"
      >
        Aucun profil ne correspond à votre recherche. Ajustez ou réinitialisez les
        filtres.
      </p>
    );
  }

  return (
    <ul
      aria-label="Profils du référentiel"
      className="grid gap-3"
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}
    >
      {profiles.map((profile) => {
        const selected = profile.profileCode === selectedProfileCode;
        return (
          <li key={profile.profileCode}>
            <button
              type="button"
              onClick={() => onSelectProfile(profile.profileCode)}
              aria-current={selected ? 'true' : undefined}
              className={cn(
                'flex w-full flex-col gap-2 rounded-lg border p-4 text-left',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                selected
                  ? 'border-primary bg-muted/50'
                  : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50',
              )}
            >
              {/* Point 20 : ligne mono — code à GAUCHE, sous-famille à DROITE (famille NON répétée). */}
              <div className="flex items-baseline justify-between gap-2 font-mono text-xs text-muted-foreground">
                <span>{profile.profileCode}</span>
                <span className="truncate text-right">{profile.sousFamille}</span>
              </div>
              {/* Point 21 : nom du profil en serif 16px. */}
              <span className="font-serif text-base font-medium text-foreground">
                {profile.libelle}
              </span>
              {/* Point 22 : chips de niveau TOUS au style NEUTRE (outline) — aucun ambre, pas de légende. */}
              <ul
                className="flex flex-wrap gap-1"
                aria-label="Niveaux d'expertise disponibles"
              >
                {profile.availableLevels.map((level) => (
                  <li key={level}>
                    <Badge variant="outline">{level}</Badge>
                  </li>
                ))}
              </ul>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
